// #84 Clocks (DC-69): the requisition's clocks and nothing else (DC-30) — three KPIs *Calendar Time · Active Time ·
// Days on Hold*, then *Time to Acceptance* against its level's target (60 / 75 / 90) and *Time to Fill* (posting to
// HRIS hire, no target), with his sentence "SLA uses Active Time; Calendar Time remains visible." Loading draws
// skeleton KPIs and rows. `unavailable` marks a clock that cannot be read (B2-5): its value is "—" and the caller's
// reason (a registered label: why the number is not there) is drawn under it; the Time to Acceptance bar is not drawn.
// The component adds no word of its own.
// #85 Legend: an inline secondary box of dot + word pairs and one sentence (O-17).

import type { ReactNode } from "react";
import { Card2, Skeleton } from "../core";
import { RULE_LABELS, SLA_LABELS, type Level, type Tone } from "../labels";
import { cx } from "../lib/cx";
import { vars } from "../lib/vars";
import { Kpi, KpiRow } from "./Kpi";
import { METRIC_LABELS, METRIC_TEMPLATES } from "./labels";

/** Time to Acceptance targets by level, in days (CLAUDE.md levels: time to fill 60 / 75 / 90, now Time to Acceptance). */
export const ACCEPTANCE_TARGET_DAYS: Readonly<Record<Level, number>> = {
  manager_and_below: 60, director_srdirector: 75, vp_and_above: 90,
};

export type ClockKey = "calendar" | "active" | "hold" | "acceptance" | "fill";

export interface ClocksProps {
  calendar: number;
  active: number;
  hold: number;
  /** Days from posting to offer accepted, or null while open. */
  acceptance?: number | null | undefined;
  /** Days from posting to HRIS hire, or null while open. */
  fill?: number | null | undefined;
  level: Level;
  /** Overrides the level's target. */
  target?: number | undefined;
  loading?: boolean | undefined;
  /** Clocks that cannot be read, each with the reason drawn under its "—" (registered words). */
  unavailable?: Partial<Record<ClockKey, string>> | undefined;
  className?: string | undefined;
}

export function Clocks({ calendar, active, hold, acceptance = null, fill = null, level, target, loading, unavailable, className }: ClocksProps) {
  const goal = target ?? ACCEPTANCE_TARGET_DAYS[level];
  const why = (key: ClockKey): string | undefined => (unavailable?.[key] ? unavailable[key] : undefined);
  const kpi = (key: "calendar" | "active" | "hold", label: string, value: number, unit?: string) => {
    const reason = why(key);
    return reason !== undefined
      ? <Kpi label={label} value="—" sub={reason} />
      : <Kpi label={label} value={value} unit={unit} />;
  };
  const rowValue = (key: "acceptance" | "fill", value: ReactNode) => {
    const reason = why(key);
    return reason !== undefined ? <>{"—"}<span className="mc-clk-why">{reason}</span></> : value;
  };
  if (loading) {
    return (
      <div className={cx("mc-clocks", className)} aria-busy="true">
        <KpiRow><Kpi label={SLA_LABELS.calendarTime} loading /><Kpi label={SLA_LABELS.activeTime} loading /><Kpi label={SLA_LABELS.daysOnHold} loading /></KpiRow>
        <Skeleton lines={2} />
      </div>
    );
  }
  const used = acceptance === null ? 0 : Math.max(0, Math.min(100, (acceptance / goal) * 100));
  return (
    <div className={cx("mc-clocks", className)}>
      <KpiRow>
        {kpi("calendar", SLA_LABELS.calendarTime, calendar, METRIC_LABELS.days)}
        {kpi("active", SLA_LABELS.activeTime, active, METRIC_LABELS.days)}
        {kpi("hold", SLA_LABELS.daysOnHold, hold)}
      </KpiRow>
      <dl className="mc-clk-rows">
        <div className="mc-clk-r">
          <dt>{METRIC_LABELS.timeToAcceptance}</dt>
          <dd>
            {why("acceptance") !== undefined ? rowValue("acceptance", null) : (
              <>
                {acceptance === null ? "—" : acceptance}{" / "}{METRIC_TEMPLATES.nDays({ n: goal })}
                <span className={cx("mc-clk-bar", acceptance !== null && acceptance > goal && "mc-clk-over")} aria-hidden="true"><b style={vars({ "--mc-pct": `${used}%` })} /></span>
              </>
            )}
          </dd>
        </div>
        <div className="mc-clk-r">
          <dt>{METRIC_LABELS.timeToFill}</dt>
          <dd>{rowValue("fill", fill === null ? "—" : METRIC_TEMPLATES.nDays({ n: fill }))}</dd>
        </div>
      </dl>
      <p className="mc-clk-foot">{RULE_LABELS.slaUsesActiveTime}</p>
    </div>
  );
}

export interface LegendItem {
  tone: Tone;
  label: string;
}

export interface LegendProps {
  items: readonly LegendItem[];
  /** The one sentence. */
  children?: ReactNode;
  className?: string | undefined;
}

export function Legend({ items, children, className }: LegendProps) {
  return (
    <Card2 tight className={cx("mc-legend", className)}>
      <ul className="mc-legend-l">
        {items.map((item, index) => (
          <li key={index}><i className={cx("mc-legend-dot", `mc-legend-${item.tone}`)} aria-hidden="true" />{item.label}</li>
        ))}
      </ul>
      {children ? <p className="mc-legend-s">{children}</p> : null}
    </Card2>
  );
}
